import { graphql } from "gatsby";
import React, { FC, useMemo } from "react";

import { useLabels } from "../../../contexts/labels";
import { MaterialType } from "../../../types/materials";
import { renameFields } from "../../../utils/object";
import { otherMaterial } from "../../forms/forms-fields/materials-form-fields";
import { ProductFinder, ProductFinderProps } from "./product-finder";

export interface ProductFinderSectionProps extends Queries.ProductFinderSectionDataFragment {}

export const ProductFinderSection: FC<ProductFinderSectionProps> = ({
  specialization,
  internalName,
  slug,
  products: _products,
}) => {
  const { labels: stringLabels } = useLabels();

  const fieldsToRename: Record<keyof ProductFinderProps["labels"], string | null> = {
    materials: null,
    [MaterialType.Wood]: "materials-wood",
    [MaterialType.Stone]: "materials-stone",
    [MaterialType.Glass]: "materials-glass",
    [MaterialType.Materia]: "materials-materia",
    [MaterialType.Metal]: "materials-metal",
    [otherMaterial]: "materials-other",
    "product-card-see-more": null,
    "product-card-compare": null,
    "product-comparator-card-empty-state": null,
    "product-comparator-compare-btn": null,
    "product-comparator-clear-btn": null,
    "product-comparator-panel-title": null,
    "product-comparator-detail-title": null,
    "product-comparator-detail-details": null,
    "product-comparator-detail-request-info": null,
    "product-comparator-detail-missing-field": null,
    "product-grid-empty-state": null,
    "product-finder-header-filter": null,
    "product-finder-panel-machine-type": null,
    "product-finder-panel-clear-filters": null,
    "product-finder-panel-apply-filters": null,
    "product-finder-panel-option-all": null,
    "product-finder-panel-no-filter-found": null,
  };
  const labels = renameFields(stringLabels, fieldsToRename) as ProductFinderProps["labels"];
  const products = useMemo(() => {
    return (_products ?? []).filter(Boolean) as Queries.ProductDataFragment[];
  }, [_products]);

  return (
    <ProductFinder
      filterSearchParamKey={slug ?? "filter"}
      products={products}
      labels={labels}
      internalName={internalName}
      specialization={specialization ?? undefined}
    />
  );
};

export const query = graphql`
  fragment AllowedValueData on ContentfulLabel {
    internalName
    value
  }
  fragment TechSheetFieldData on ContentfulTechSheetField {
    internalName
    label
    valueType
    allowedValues {
      ...AllowedValueData
    }
  }
  fragment MachineSpecializationData on ContentfulMachineSpecialization {
    id
    node_locale
    name
    techSheet {
      ...TechSheetFieldData
    }
    machiningType {
      name
      id
      material
    }
  }
  fragment ProductData on ContentfulProduct {
    id
    name
    node_locale
    shortDescription {
      raw
    }
    cardImages {
      gatsbyImageData
    }
    category
    techSheet {
      internal {
        content
      }
    }
    specialization {
      ...MachineSpecializationData
    }
    standardpage {
      id
    }
    order
    requestInfoFormCta {
      ...CtaData
    }
    defaultLocaleEntry {
      name
      id
      specialization {
        ...MachineSpecializationData
      }
    }
  }
  fragment ProductFinderSectionData on ContentfulProductFinderSection {
    slug
    internalName
    specialization {
      ...MachineSpecializationData
    }
    products {
      ...ProductData
    }
  }
`;
